import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import API_URL from '../api/baseURL';

const VehicleSearchBar = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch assigned vehicles once
  useEffect(() => {
    if (!token || !user?._id) return;
    setLoading(true);
    axios.get(`${API_URL}/vehicles/assigned/${user._id}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => setVehicles(Array.isArray(res.data) ? res.data : []))
      .catch(() => setVehicles([]))
      .finally(() => setLoading(false));
  }, [token, user]);

  // Filter on query change
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    const q = query.toLowerCase();
    const filtered = vehicles.filter(v =>
      (v.plateNumber || '').toLowerCase().includes(q) ||
      (v.brand || '').toLowerCase().includes(q) ||
      (v.model || '').toLowerCase().includes(q) ||
      (v.owner?.name || '').toLowerCase().includes(q)
    ).slice(0, 8);
    setResults(filtered);
  }, [query, vehicles]);

  // Close on click outside
  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = (vehicle) => {
    setIsOpen(false);
    setQuery('');
    navigate(`/workshop/history/${vehicle._id}`);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center bg-slate-800 border border-slate-700 rounded-lg overflow-hidden hover:border-slate-600 transition-colors">
        <svg className="w-4 h-4 text-slate-500 ml-2.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
          placeholder="Plaque, marque, client..."
          className="bg-transparent text-white text-sm px-2 py-1.5 w-32 sm:w-44 outline-none placeholder-slate-500"
        />
      </div>

      {/* Dropdown results */}
      {isOpen && query.trim() && (
        <div className="absolute z-50 mt-1 w-72 right-0 bg-slate-900 border border-slate-700 rounded-lg shadow-xl overflow-hidden">
          {loading ? (
            <div className="px-3 py-3 text-sm text-slate-500 text-center">Chargement...</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-3 text-sm text-slate-500 text-center">Aucun résultat</div>
          ) : (
            <ul className="max-h-64 overflow-y-auto">
              {results.map(v => (
                <li
                  key={v._id}
                  onClick={() => handleSelect(v)}
                  className="px-3 py-2.5 cursor-pointer hover:bg-slate-800 transition-colors border-b border-slate-800 last:border-0"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-white text-sm font-semibold">{v.plateNumber}</span>
                    <span className="text-slate-400 text-xs">{v.brand} {v.model}</span>
                  </div>
                  {v.owner?.name && (
                    <div className="text-slate-500 text-xs mt-0.5">{v.owner.name}</div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default VehicleSearchBar;
